import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { supabase } from "../../logic/client";
import { useI18n } from "../../i18n/I18nProvider";


type StepId = "q1" | "q2" | "q3" | "q4" | "q5";

const ORDER: StepId[] = ["q1", "q2", "q3", "q4", "q5"];

type AvailabilityQuestions = {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  q5: string;
};

type AvailabilityAnswerValue = "N/A" | "yes" | "no" | "prefer_not_to_say";

type FormState = {
  q1: 0 | 1 | null;
  q2: 0 | 1 | null;
  q3: 0 | 1 | null;
  q4: "yes" | "no" | "pns" | null;
  q5: 0 | 1 | null;
};

const DEFAULT_FORM: FormState = {
  q1: null,
  q2: null,
  q3: null,
  q4: null,
  q5: null,
};

const QUESTIONS_DE: AvailabilityQuestions = {
  q1: "Wohnst du weniger als 15 km von deinem Arbeitsplatz entfernt?",
  q2: "Bist du auf öffentliche Verkehrsmittel angewiesen, um zur Arbeit zu kommen?",
  q3: "Hast du Angehörige, um die du alleine kümmern musst?",
  q4: "Bist du Teil einer ehrenamtlichen Notfallorganisation? (z. B. THW, Militär, Rotes Kreuz, Sonstige)",
  q5: "Hast du gesundheitliche Einschränkungen, die deine Verfügbarkeit beeinflussen könnten?",
};

function formToAnswers(form: FormState): Record<StepId, AvailabilityAnswerValue> {
  return {
    q1: form.q1 === null ? "N/A" : form.q1 === 1 ? "yes" : "no",
    q2: form.q2 === null ? "N/A" : form.q2 === 1 ? "yes" : "no",
    q3: form.q3 === null ? "N/A" : form.q3 === 1 ? "yes" : "no",
    q4:
      form.q4 === null
        ? "N/A"
        : form.q4 === "pns"
        ? "prefer_not_to_say"
        : form.q4,
    q5: form.q5 === null ? "N/A" : form.q5 === 1 ? "yes" : "no",
  };
}

function answersToForm(answers: any): FormState {
  const a = {
    q1: answers?.q1 ?? "N/A",
    q2: answers?.q2 ?? "N/A",
    q3: answers?.q3 ?? "N/A",
    q4: answers?.q4 ?? "N/A",
    q5: answers?.q5 ?? "N/A",
  };

  const toBinary = (v: AvailabilityAnswerValue): 0 | 1 | null => {
    if (v === "yes") return 1;
    if (v === "no") return 0;
    return null;
  };

  return {
    q1: toBinary(a.q1),
    q2: toBinary(a.q2),
    q3: toBinary(a.q3),
    q4:
      a.q4 === "yes"
        ? "yes"
        : a.q4 === "no"
        ? "no"
        : a.q4 === "prefer_not_to_say"
        ? "pns"
        : null,
    q5: toBinary(a.q5),
  };
}

export default function AvailabilityCheck(props: {
  handlePageChange: (page: string) => void;
}) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const isDE = lang === "de";

  const [stepIndex, setStepIndex] = React.useState(0);
  const [questions, setQuestions] = React.useState<AvailabilityQuestions | null>(null);
  const [form, setForm] = React.useState<FormState>(DEFAULT_FORM);

  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const currentStep = ORDER[stepIndex];
  const progress = ((stepIndex + 1) / ORDER.length) * 100;

  const loadInitialData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userRes?.user?.id) {
        throw new Error(isDE ? "Benutzer nicht gefunden." : "User not found.");
      }

      const { data: emp, error: empErr } = await supabase
        .from("employees")
        .select("id")
        .eq("auth_user_id", userRes.user.id)
        .single();

      if (empErr || !emp) {
        throw new Error(isDE ? "Mitarbeiterprofil nicht gefunden." : "Employee profile not found.");
      }

      setEmployeeId(emp.id);

      const { data: qRow, error: qErr } = await supabase
        .from("availability_questions")
        .select("q1, q2, q3, q4, q5, created_at")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (qErr || !qRow) {
        throw new Error(isDE ? "Fragen konnten nicht geladen werden." : "Failed to load questions.");
      }

      const englishQuestions: AvailabilityQuestions = {
        q1: qRow.q1,
        q2: qRow.q2,
        q3: qRow.q3,
        q4: qRow.q4,
        q5: qRow.q5,
      };

      setQuestions(englishQuestions);

      const { data: existing, error: existingErr } = await supabase
        .from("employee_availability")
        .select("answers")
        .eq("employee_id", emp.id)
        .maybeSingle();

      if (existingErr) {
        throw new Error(isDE ? "Vorherige Antworten konnten nicht geladen werden." : "Failed to load existing answers.");
      }

      if (existing?.answers) {
        setForm(answersToForm(existing.answers));
      }
    } catch (err: any) {
      setError(err?.message ?? (isDE ? "Ein Fehler ist aufgetreten." : "Something went wrong."));
    } finally {
      setLoading(false);
    }
  }, [isDE]);

  React.useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  function getQuestionText(step: StepId) {
    if (!questions) return "";
    if (isDE) return QUESTIONS_DE[step];
    return questions[step];
  }

  function canContinue() {
    if (currentStep === "q4") return form.q4 !== null;
    return form[currentStep] !== null;
  }

  async function persistAnswers(nextForm: FormState) {
    if (!employeeId) return;

    const payload = formToAnswers(nextForm);

    const { data: existing } = await supabase
      .from("employee_availability")
      .select("employee_id")
      .eq("employee_id", employeeId)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from("employee_availability")
        .update({ answers: payload })
        .eq("employee_id", employeeId);

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("employee_availability")
        .insert({
          employee_id: employeeId,
          answers: payload,
        });

      if (error) throw error;
    }
  }

  async function setAnswer(step: StepId, value: any) {
    const nextForm = { ...form, [step]: value };
    setForm(nextForm);

    try {
      setSaving(true);
      setError(null);
      await persistAnswers(nextForm);
    } catch (err: any) {
      setError(
        err?.message ??
          (isDE ? "Antwort konnte nicht gespeichert werden." : "Failed to save answer.")
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    try {
      setSubmitting(true);
      setError(null);

      await persistAnswers(form);

      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr) throw sessionErr;

      const token = sessionData.session?.access_token;
      if (!token) {
        throw new Error(isDE ? "Keine Sitzung gefunden." : "No session found.");
      }

      await supabase.functions.invoke("compute-kontrol-score", {
        headers: { Authorization: `Bearer ${token}` },
        body: {},
      });

      handlePageChange("EmployeeScore");
    } catch (err: any) {
      setError(
        err?.message ??
          (isDE ? "Absenden fehlgeschlagen." : "Failed to submit.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderAnswerBlock() {
    if (currentStep === "q4") {
      return (
        <ToggleButtonGroup
          exclusive
          value={form.q4}
          onChange={(_, value) => {
            if (value === "yes" || value === "no" || value === "pns") {
              setAnswer("q4", value);
            }
          }}
          sx={toggleGroupSx}
        >
          <ToggleButton value="yes">{isDE ? "Ja" : "Yes"}</ToggleButton>
          <ToggleButton value="no">{isDE ? "Nein" : "No"}</ToggleButton>
          <ToggleButton value="pns">
            {isDE ? "Möchte ich nicht sagen" : "Prefer not to say"}
          </ToggleButton>
        </ToggleButtonGroup>
      );
    }

    const value = form[currentStep] === null ? null : String(form[currentStep]);

    return (
      <ToggleButtonGroup
        exclusive
        value={value}
        onChange={(_, nextValue) => {
          if (nextValue === "1" || nextValue === "0") {
            setAnswer(currentStep, Number(nextValue) as 0 | 1);
          }
        }}
        sx={toggleGroupSx}
      >
        <ToggleButton value="1">{isDE ? "Ja" : "Yes"}</ToggleButton>
        <ToggleButton value="0">{isDE ? "Nein" : "No"}</ToggleButton>
      </ToggleButtonGroup>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          px: 2,
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
          `,
          backgroundImage: `
            linear-gradient(
              rgba(8, 8, 8, 0.78),
              rgba(8, 8, 8, 0.82)
            ),
            url("/scenarioBgs/availability.png")
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          }}
      >
        <Paper
          sx={{
            width: "min(900px, 90vw)",
            p: 5,
            borderRadius: 5,
            border: "1px solid rgba(255,255,255,0.14)",
            background:
              "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
            boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#fff" }}>
            {isDE ? "Lade Verfügbarkeits-Check..." : "Loading availability check..."}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/availability.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: "min(920px, 92vw)",
          borderRadius: 5,
          p: { xs: 3, sm: 5, md: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >
        <Stack spacing={4}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: { xs: "1.5rem", sm: "1.9rem" },
                }}
              >
                {isDE ? "Verfügbarkeits-Check" : "Availability Check"}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.72)",
                  mt: 0.7,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                }}
              >
                {isDE
                  ? "Beantworte 5 kurze Fragen nacheinander."
                  : "Answer 5 short questions one by one."}
              </Typography>
            </Box>

            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={`${stepIndex + 1} / ${ORDER.length}`}
              sx={{
                bgcolor: "rgba(255,255,255,0.08)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.12)",
                "& .MuiChip-icon": { color: "#ff8a8a" },
                fontWeight: 700,
              }}
            />
          </Stack>

          <Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 99,
                backgroundColor: "rgba(255,255,255,0.1)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  background: "linear-gradient(90deg, #b50d20, #d6293e)",
                },
              }}
            />
          </Box>

          {error && (
            <Typography
              sx={{
                color: "#ff8a8a",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              {error}
            </Typography>
          )}

          <Box
            sx={{
              minHeight: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: { xs: 1, sm: 4 },
            }}
          >
            <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: { xs: "1.4rem", sm: "2rem" },
                  lineHeight: 1.35,
                  maxWidth: 760,
                }}
              >
                {getQuestionText(currentStep)}
              </Typography>

              {renderAnswerBlock()}

              {saving && (
                <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
                  {isDE ? "Speichert..." : "Saving..."}
                </Typography>
              )}
            </Stack>
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
          >
            <Button
              variant="outlined"
              onClick={() => {
                if (stepIndex === 0) {
                  handlePageChange("AvailabilityAwareness");
                } else {
                  setStepIndex((prev) => Math.max(prev - 1, 0));
                }
              }}
              startIcon={<ArrowBackIcon />}
              sx={{
                borderColor: "rgba(255,255,255,0.35)",
                color: "#fff",
                px: 2.5,
                py: 1.2,
                fontWeight: 700,
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              {isDE ? "Zurück" : "Back"}
            </Button>

            {stepIndex < ORDER.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setStepIndex((prev) => Math.min(prev + 1, ORDER.length - 1))}
                disabled={!canContinue() || saving}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: "linear-gradient(90deg, #b50d20, #d6293e)",
                  px: 2.8,
                  py: 1.2,
                  fontWeight: 700,
                  "&.Mui-disabled": {
                    background: "rgba(255,255,255,0.18)",
                    color: "rgba(255,255,255,0.4)",
                  },
                }}
              >
                {isDE ? "Weiter" : "Continue"}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canContinue() || submitting || saving}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: "linear-gradient(90deg, #b50d20, #d6293e)",
                  px: 2.8,
                  py: 1.2,
                  fontWeight: 700,
                  "&.Mui-disabled": {
                    background: "rgba(255,255,255,0.18)",
                    color: "rgba(255,255,255,0.4)",
                  },
                }}
              >
                {submitting
                  ? isDE
                    ? "Score wird kalkuliert..."
                    : "Score being calculated..."
                  : isDE
                  ? "Kalkuliere mein Score"
                  : "Calculate my score"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

const toggleGroupSx = {
  display: "flex",
  justifyContent: "center",
  gap: 1.5,
  flexWrap: "wrap",
  "& .MuiToggleButtonGroup-grouped": {
    margin: 0,
    borderRadius: "16px !important",
    border: "1px solid rgba(255,255,255,0.16) !important",
  },
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 800,
    fontSize: "0.98rem",
    px: 3,
    py: 1.4,
    minWidth: 130,
    color: "#fff",
    background: "rgba(255,255,255,0.04)",
    transition: "all .2s ease",
  },
  "& .MuiToggleButton-root:hover": {
    background: "rgba(255,255,255,0.08)",
  },
  "& .Mui-selected": {
    background: "linear-gradient(90deg, #b50d20, #d6293e) !important",
    color: "#fff !important",
    borderColor: "transparent !important",
  },
} as const;